import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { teacherSettingsApi } from '@/services/api/teacher'
import type { TeacherSettingDefinition, TeacherSettingsResponse } from '@/types/teacher-request'
import { AlertTriangle, Settings, Save, RotateCcw, Check } from 'lucide-react'
import { TeacherSettingsView } from '@/components/teacher-settings/TeacherSettingsView'

function slugify(str: string): string {
  return str.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
}

export function TeacherSettingsPage() {
  const queryClient = useQueryClient()
  const [overrides, setOverrides] = useState<Record<string, unknown>>({})
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set())
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const [activeCategory, setActiveCategory] = useState<string>('Prioritätsanfragen')
  const [activeSubgroup, setActiveSubgroup] = useState<string>('')

  const { data, isLoading, error, refetch } = useQuery<TeacherSettingsResponse>({
    queryKey: ['my-settings'],
    queryFn: () => teacherSettingsApi.getMySettings(),
    staleTime: 15 * 1000,
    retry: 1,
  })

  const dirtyFieldsRef = useRef(dirtyFields)
  dirtyFieldsRef.current = dirtyFields

  useEffect(() => {
    if (data?.teacher_overrides && dirtyFieldsRef.current.size === 0) {
      setOverrides(data.teacher_overrides)
    }
  }, [data])

  useEffect(() => {
    if (!data?.categories) return
    const firstCategory = data.categories[0]?.name || 'Prioritätsanfragen'
    const firstSubgroup = data.categories.find(c => c.name === firstCategory)?.subgroups[0]?.name || ''
    setActiveCategory(prev => data.categories.some(c => c.name === prev) ? prev : firstCategory)
    setActiveSubgroup(prev => prev || firstSubgroup)
  }, [data])

  useEffect(() => {
    if (!data?.categories) return

    const syncHashToSection = () => {
      const hash = window.location.hash.slice(1)
      if (hash) {
        const parts = hash.split('/')
        const categorySlug = parts[0]
        const subgroupSlug = parts[1]

        const matchedCategory = data.categories.find(c =>
          slugify(c.name) === categorySlug
        )
        if (matchedCategory) {
          setActiveCategory(matchedCategory.name)
          if (subgroupSlug) {
            const matchedSubgroup = matchedCategory.subgroups.find(sg =>
              slugify(sg.name) === subgroupSlug
            )
            if (matchedSubgroup) {
              setActiveSubgroup(matchedSubgroup.name)
              setTimeout(() => {
                document.querySelector(`[data-subgroup="${CSS.escape(matchedSubgroup.name)}"]`)?.scrollIntoView({ behavior: 'smooth' })
              }, 100)
            }
          }
        }
      }
    }

    syncHashToSection()
    window.addEventListener('hashchange', syncHashToSection)
    return () => window.removeEventListener('hashchange', syncHashToSection)
  }, [data])

  const handleCategoryChange = (categoryName: string) => {
    setActiveCategory(categoryName)
    const category = data?.categories.find(c => c.name === categoryName)
    if (category?.subgroups[0]) {
      setActiveSubgroup(category.subgroups[0].name)
    }
    window.location.hash = slugify(categoryName)
  }

  const handleSubgroupChange = (subgroupName: string) => {
    setActiveSubgroup(subgroupName)
    window.location.hash = `${slugify(activeCategory)}/${slugify(subgroupName)}`
  }

  const saveMutation = useMutation({
    mutationFn: (ovr: Record<string, unknown>) => teacherSettingsApi.updateMySettings(ovr),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-settings'] })
      setDirtyFields(new Set())
      setSaveMessage('Einstellungen gespeichert')
      setTimeout(() => setSaveMessage(null), 3000)
    },
    onError: (err: Error) => {
      setSaveMessage(null)
      setValidationErrors({ _form: err.message || 'Fehler beim Speichern' })
    },
  })

  const resetMutation = useMutation({
    mutationFn: (key: string) => teacherSettingsApi.resetMySetting(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-settings'] })
    },
  })

  const resetAllMutation = useMutation({
    mutationFn: () => teacherSettingsApi.resetAllMySettings(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-settings'] })
      setOverrides({})
      setDirtyFields(new Set())
    },
  })

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-40 bg-gray-200 rounded"></div>
          <div className="h-40 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-700 font-medium">Einstellungen konnten nicht geladen werden</p>
          <button onClick={() => refetch()} className="mt-3 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition">
            Erneut versuchen
          </button>
        </div>
      </div>
    )
  }

  const { editable_keys } = data
  const editableSet = new Set(editable_keys)
  const hasEditableKeys = editable_keys.length > 0
  const definitionsByKey: Record<string, TeacherSettingDefinition> = {}
  for (const d of data.definitions) {
    definitionsByKey[d.key] = d
  }

  const updateOverride = (key: string, value: unknown) => {
    const defn = definitionsByKey[key]
    if (!defn || defn.readonly) return

    let errorMsg: string | null = null
    if (defn.type === 'int') {
      if (typeof value !== 'number' || isNaN(value)) {
        errorMsg = 'Muss eine Zahl sein'
      } else {
        if (defn.min_value !== null && value < defn.min_value) errorMsg = `Mindestens ${defn.min_value}`
        if (defn.max_value !== null && value > defn.max_value) errorMsg = `Hoechstens ${defn.max_value}`
      }
    } else if (defn.type === 'float') {
      if (typeof value !== 'number' || isNaN(value)) {
        errorMsg = 'Muss eine Zahl sein'
      } else {
        if (defn.min_value !== null && value < defn.min_value) errorMsg = `Mindestens ${defn.min_value}`
        if (defn.max_value !== null && value > defn.max_value) errorMsg = `Hoechstens ${defn.max_value}`
      }
    } else if ((defn.type === 'string' || defn.type === 'str') && defn.choices) {
      if (typeof value !== 'string' || !defn.choices.includes(value)) {
        errorMsg = `Muss einer von ${defn.choices.join(', ')} sein`
      }
    } else if (defn.type === 'json_list') {
      if (!Array.isArray(value)) {
        errorMsg = 'Muss eine Liste sein'
      } else if (defn.choices) {
        for (const item of value) {
          if (!defn.choices.includes(item)) {
            errorMsg = `Element '${item}' ist nicht gültig`
            break
          }
        }
        if (!errorMsg && new Set(value).size !== value.length) {
          errorMsg = 'Keine Duplikate erlaubt'
        }
      }
    }

    if (errorMsg) {
      setValidationErrors(prev => ({ ...prev, [key]: errorMsg! }))
      return
    }

    setValidationErrors(prev => {
      const next = { ...prev }
      delete next[key]
      return next
    })
    setOverrides(prev => ({ ...prev, [key]: value }))
    setDirtyFields(prev => new Set(prev).add(key))
  }

  const handleReset = (key: string) => {
    setOverrides(prev => {
      const next = { ...prev }
      delete next[key]
      return next
    })
    setDirtyFields(prev => {
      const next = new Set(prev)
      next.delete(key)
      return next
    })
    setValidationErrors(prev => {
      const next = { ...prev }
      delete next[key]
      return next
    })
    resetMutation.mutate(key)
  }

  const handleResetAll = () => {
    if (confirm('Alle personalisierten Einstellungen zurücksetzen?')) {
      resetAllMutation.mutate()
    }
  }

  const handleSave = () => {
    const toSave: Record<string, unknown> = {}
    for (const key of dirtyFields) {
      if (key in overrides && editableSet.has(key)) {
        toSave[key] = overrides[key]
      }
    }
    if (Object.keys(toSave).length === 0) return
    saveMutation.mutate(toSave)
  }

  const hasErrors = Object.keys(validationErrors).length > 0

  return (
    <div className="flex flex-col min-h-full">
      <div className="px-6 py-4 border-b bg-white flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <Settings className="w-5 h-5 text-gray-500" />
          <div>
            <h1 className="text-xl font-bold">Einstellungen</h1>
            <p className="text-xs text-gray-500">
              {hasEditableKeys
                ? 'Personalisierte Werte sind blau markiert.'
                : 'Anzeige der Schul-Konfiguration.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {hasEditableKeys && Object.keys(overrides).length > 0 && (
            <button onClick={handleResetAll} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
              <RotateCcw className="w-3 h-3" /> Alle zurücksetzen
            </button>
          )}
          {hasEditableKeys && dirtyFields.size > 0 && (
            <button
              onClick={handleSave}
              disabled={hasErrors || saveMutation.isPending}
              className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
            >
              <Save className="w-3.5 h-3.5" />
              {saveMutation.isPending ? 'Speichern...' : 'Speichern'}
            </button>
          )}
        </div>
      </div>

      {saveMessage && (
        <div className="bg-green-50 border-b border-green-200 px-6 py-3 flex items-center gap-2 text-sm text-green-700">
          <Check className="w-4 h-4" /> {saveMessage}
        </div>
      )}

      {validationErrors._form && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-3 flex items-center gap-2 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4" /> {validationErrors._form}
        </div>
      )}

      <TeacherSettingsView
        data={data}
        editable={true}
        overrides={overrides}
        validationErrors={validationErrors}
        onUpdateOverride={updateOverride}
        onResetKey={handleReset}
        activeCategory={activeCategory}
        activeSubgroup={activeSubgroup}
        onCategoryChange={handleCategoryChange}
        onSubgroupChange={handleSubgroupChange}
      />
    </div>
  )
}
